import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { Role, RoleExtended } from '@/features/managed-accounts'
import { useAppSelector } from '@/redux/hooks'
import { selectEmail, selectRoles } from '@/redux/me/meSlice'

const HIVEWATCH_DOMAIN = '@hivewatch.com'

export const usePermissions = () => {
  const roles = useAppSelector(selectRoles)
  const email = useAppSelector(selectEmail)

  const isCustomerSecretProviderEnabled = useFeatureFlag(
    FeatureFlag.customerSecretProvider
  )

  // Roles - check if user has ANY of these roles
  const isPM = roles.includes(Role.PROGRAM_MANAGER)
  const isImplementer = roles.includes(Role.IMPLEMENTER)
  const isInternalImplementer =
    roles.includes(Role.IMPLEMENTER) &&
    Boolean(email?.endsWith(HIVEWATCH_DOMAIN))
  const isReadOnly = roles.includes(Role.READ_ONLY_USER)
  const isAdmin = isImplementer || isPM || isReadOnly
  const isOperator = roles.includes(Role.OPERATOR)

  // Page access
  const shouldShowDashboardPage = isPM || isReadOnly

  // Be careful removing this! Even without Edit Mode as it once existed, some places don't do permission checks and assume that this gate would block non PMs & IMPLs from accessing it
  const shouldShowEditMode = isImplementer || isPM || isReadOnly
  const shouldShowSettingsPage = isImplementer || isPM || isReadOnly
  const shouldShowIncidentRulesPage = isPM || isOperator || isImplementer
  const shouldShowCaseManagementPage = isPM || isOperator

  // Incident actions
  const shouldEnableIncidentActions = !isReadOnly
  const shouldEnableIncidentRadioGuards = !isReadOnly
  const shouldEnableIncidentEmergencyCall = !isReadOnly
  const shouldEnableIncidentNote = !isReadOnly
  const shouldShowDownloadReportButton = isOperator || isPM
  const canBulkAction = isImplementer || isPM || isOperator

  //Modals
  const shouldEnableRemoveModal = !isReadOnly

  // Incident
  const shouldEnableCreateIncident = !isReadOnly
  const shouldShowCreateManualIncidentCategory = isImplementer || isPM

  // Device
  const shouldEnableCreateDevice = isImplementer
  const shouldEnableUpdateDevice = isImplementer || isPM
  const shouldEnableDeleteDevice = isImplementer
  const shouldShowExternalAccountFields = isImplementer
  // Associate Devices
  const shouldEnableAssociateDevices = isImplementer || isPM
  const shouldEnableConfigureTailgateGrid = isImplementer || isPM
  // Facility
  const shouldEnableCreateFacility = isImplementer
  const shouldEnableUpdateFacility = isImplementer || isPM
  const shouldEnableUpdateFacilityAddress = isImplementer
  const shouldEnableDeleteFacility = isImplementer
  // Floor
  const shouldEnableCreateFloor = isImplementer || isPM
  const shouldEnableUpdateFloor = isImplementer || isPM
  // Checkpoint
  const shouldEnableCreateCheckpoint = isImplementer || isPM
  const shouldEnableUpdateCheckpoint = isImplementer || isPM
  const shouldEnableDeleteCheckpoint = isImplementer || isPM
  const shouldEnableCheckpointChecklist = isPM
  // TourPlan
  const shouldEnableCreateTourPlan = isImplementer || isPM
  const shouldEnableUpdateTourPlan = isImplementer || isPM
  const shouldEnableDeleteTourPlan = isImplementer || isPM
  // SOP
  const shouldEnableCreateSOP = isPM || isImplementer
  const shouldEnableUpdateSOP = isPM || isImplementer
  const shouldShowCreateWorkflow = isPM || isImplementer
  // Incident Rules
  const shouldEnableCreateIncidentsRule = isPM || isImplementer
  const shouldEnableEditIncidentRule = isPM || isImplementer
  // Gateway Devices
  const shouldEnableCreateGateway = isImplementer
  const shouldEnableUpdateGateway = isImplementer || isPM
  const shouldEnableRestartGateway = isImplementer
  // PACS
  const shouldEnableCreatePACS = isImplementer
  // User, Technician
  const shouldEnableCreateUser = isImplementer || isPM
  const shouldEnableUpdateUser = isImplementer || isPM
  const shouldEnableCreateTechnician = isImplementer || isPM
  const shouldEnableUpdateTechnician = isImplementer || isPM
  // Setting - DatabaseReset, DeviceHealth Setting, Organization Settings
  const shouldEnableResetDatabase = isPM
  const shouldEnableUpdateDeviceHealthSettings = isImplementer || isPM
  const shouldEnableUpdateOrganizationSettings = isImplementer || isPM
  const shouldShowPACSSourceSecretsSettings = isImplementer
  const shouldShowExternalAccountSettings = isImplementer
  const shouldShowCustomerSecretProviderSettings =
    isImplementer && isCustomerSecretProviderEnabled

  // Program Recommendations
  const shouldShowProgramRecommendationsPage =
    isPM || isOperator || isImplementer
  const shouldShowImplementerOnlyTabs = isImplementer
  const shouldEnableRecommendationsUpdate = isImplementer || isPM

  //Case Management
  const shouldEnableCaseManagement = isPM || isOperator
  const shouldEnableCaseDownload = isPM

  //QuickReport
  const shouldShowQuickReport = isPM || isImplementer
  const shouldEnableCreateQuickReportCategory = isPM || isImplementer
  const shouldEnableWhitelistedUsersForIncidentCategory = isImplementer
  const shouldEditQuickReportCategory = isPM || isImplementer

  //Send to Ontic
  const shouldEnableSendToOntic = isPM || isOperator

  //Ai operator
  const shouldUpdateAiLimit = isImplementer
  const isAiOperatorHubEnabled = useFeatureFlag(FeatureFlag.aiOperatorHub)
  const isAioPmHubEnabled = useFeatureFlag(FeatureFlag.aioPmHub)
  const shouldShowOperatorHubPage =
    (isImplementer && isAiOperatorHubEnabled) || (isPM && isAioPmHubEnabled)
  const shouldShowOperatorHub = isPM && isAioPmHubEnabled

  // Metrics button on the incidents page
  const shouldShowMetricsBtn = isPM

  // Announcement Banner Settings
  const shouldShowAnnouncementSettings = isImplementer || isPM

  // Labels
  const shouldManageLabels = isImplementer || isPM

  /**
   * This checks if a user has permission to add/edit/delete the specified role
   * crudRole - the role to perform the CRUD operation on
   */
  const hasCRUDPermission = (crudRole: Role | RoleExtended) => {
    if (roles.includes(Role.IMPLEMENTER)) {
      return true
    } else if (
      roles.includes(Role.PROGRAM_MANAGER) &&
      crudRole !== Role.IMPLEMENTER
    ) {
      return true
    }
    return false
  }

  return {
    hasCRUDPermission,
    isAdmin,
    isImplementer,
    isInternalImplementer,
    isPM,
    shouldEnableAssociateDevices,
    shouldEnableCaseDownload,
    shouldEnableCaseManagement,
    shouldEnableConfigureTailgateGrid,
    shouldEnableCreateCheckpoint,
    shouldEnableCreateDevice,
    shouldEnableCreateFacility,
    shouldEnableCreateFloor,
    shouldEnableCreateGateway,
    shouldEnableCreateIncident,
    shouldEnableCreateIncidentsRule,
    shouldEnableEditIncidentRule,
    shouldEnableCreatePACS,
    shouldEnableCreateSOP,
    shouldEnableCreateTechnician,
    shouldEnableCreateTourPlan,
    shouldEnableCreateUser,
    shouldEnableDeleteCheckpoint,
    shouldEnableDeleteDevice,
    shouldEnableDeleteFacility,
    shouldEnableDeleteTourPlan,
    shouldEnableIncidentActions,
    shouldEnableIncidentEmergencyCall,
    shouldEnableIncidentNote,
    shouldEnableIncidentRadioGuards,
    shouldEnableRemoveModal,
    shouldEnableResetDatabase,
    shouldEnableRestartGateway,
    shouldEnableUpdateCheckpoint,
    shouldEnableUpdateDevice,
    shouldEnableUpdateDeviceHealthSettings,
    shouldEnableUpdateFacility,
    shouldEnableUpdateFacilityAddress,
    shouldEnableUpdateFloor,
    shouldEnableUpdateGateway,
    shouldEnableUpdateOrganizationSettings,
    shouldEnableUpdateSOP,
    shouldEnableUpdateTechnician,
    shouldEnableUpdateTourPlan,
    shouldEnableUpdateUser,
    shouldShowCreateManualIncidentCategory,
    shouldShowDashboardPage,
    shouldShowDownloadReportButton,
    canBulkAction,
    shouldShowEditMode,
    shouldShowIncidentRulesPage,
    shouldShowPACSSourceSecretsSettings,
    shouldShowSettingsPage,
    shouldShowCaseManagementPage,
    shouldShowOperatorHubPage,
    shouldShowOperatorHub,
    shouldShowExternalAccountSettings,
    shouldShowExternalAccountFields,
    shouldShowCustomerSecretProviderSettings,
    shouldShowQuickReport,
    shouldEnableSendToOntic,
    shouldEnableCreateQuickReportCategory,
    shouldEnableWhitelistedUsersForIncidentCategory,
    shouldEditQuickReportCategory,
    shouldEnableCheckpointChecklist,
    shouldUpdateAiLimit,
    shouldShowMetricsBtn,
    shouldShowAnnouncementSettings,
    shouldManageLabels,
    shouldShowProgramRecommendationsPage,
    shouldShowImplementerOnlyTabs,
    shouldEnableRecommendationsUpdate,
    shouldShowCreateWorkflow,
  }
}
